import React from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { SearchBy, UserAdminInfo } from "../../api/users";
import { revenueCatCustomerUrl } from "../../api/constants";
import {
  EMPTY_SEARCH,
  UserFilters,
  UserSearch,
  useUsersStore,
} from "../../store/usersStore";
import { useDialogState } from "../../hooks/useDialogState";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { FilterChip } from "../common/FilterChip";
import DelayedInput from "../common/DelayedInput";
import { UserActionsMenu } from "./user_actions/UserActionsMenu";
import { UserDetailDialog } from "./UserDetailDialog";

const SEARCH_MODES: {
  value: SearchBy;
  label: string;
  hint?: string;
}[] = [
  {
    value: "auto",
    label: "Auto — detect",
    hint: "An id, an email, or part of a name",
  },
  { value: "email", label: "Email", hint: "Starts with" },
  {
    value: "displayName",
    label: "Display name",
    hint: "Contains — matching is case-insensitive for ordinary capitalisation",
  },
  { value: "userId", label: "User id", hint: "Cognito sub or fsId" },
  { value: "revenuecatId", label: "RevenueCat id", hint: "Exact match" },
];

const RC_STORE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "app_store", label: "App Store (iOS)" },
  { value: "play_store", label: "Play Store (Android)" },
  { value: "stripe", label: "Stripe (Web)" },
  { value: "promotional", label: "Promotional" },
];

const RC_STATUS_OPTIONS = [
  { value: "", label: "Any" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "intro", label: "Intro" },
  { value: "grace_period", label: "Grace Period" },
  { value: "expired", label: "Expired" },
  { value: "no_subscription", label: "No subscription" },
];

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The most common empty result is a term that belongs to a different mode, so
 * name the mode that would have found it instead of leaving the admin to guess.
 */
const suggestModeFor = (search: UserSearch): SearchBy | undefined => {
  if (search.searchBy === "auto" || !search.q) {
    return undefined;
  }
  if (search.q.includes("@") && search.searchBy !== "email") {
    return "email";
  }
  if (UUID_PATTERN.test(search.q) && search.searchBy !== "userId") {
    return "auto";
  }
  return undefined;
};

const shortId = (id: string) => (id.length > 12 ? `${id.slice(0, 8)}…` : id);

export const UserList = () => {
  const {
    users,
    nextToken,
    loading,
    truncated,
    loadUsers,
    loadNextPage,
  } = useUsersStore();
  const [search, setSearch] = React.useState<UserSearch>(EMPTY_SEARCH);
  const [filters, setFilters] = React.useState<UserFilters>({});
  // Bumped when the search is cleared: DelayedInput holds its own text state,
  // so it has to be remounted to drop what the user typed.
  const [resetKey, setResetKey] = React.useState(0);
  const detailDialog = useDialogState<UserAdminInfo>();

  React.useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySearch = React.useCallback(
    (patch: Partial<UserSearch>) => {
      const next = { ...search, ...patch };
      setSearch(next);
      loadUsers(next, filters);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, filters]
  );

  const applyFilters = React.useCallback(
    (patch: Partial<UserFilters>) => {
      const next = { ...filters, ...patch };
      setFilters(next);
      loadUsers(search, next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, filters]
  );

  // The guard keeps DelayedInput's on-mount callback from re-fetching with an
  // unchanged value.
  const handleQueryChange = React.useCallback(
    (text: string) => {
      if (text === (search.q ?? "")) {
        return;
      }
      applySearch({ q: text || undefined });
    },
    [applySearch, search.q]
  );

  const activeMode = SEARCH_MODES.find((mode) => mode.value === search.searchBy);

  const columns = [
    { id: "email", label: "Email" },
    { id: "displayName", label: "Name", hideOnMobile: true },
    {
      id: "fsId",
      label: "App user id",
      hideOnMobile: true,
      render: (user: UserAdminInfo) =>
        user.fsId ? (
          <Tooltip title={user.fsId}>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {shortId(user.fsId)}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      id: "accountState",
      label: "State",
      render: (user: UserAdminInfo) => (
        <Typography
          sx={{
            color: user.accountState === "CONFIRMED" ? "green" : "red",
          }}
        >
          {user.accountState}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      hideOnMobile: true,
      render: (user: UserAdminInfo) => {
        const rcUrl = user.revenueCatId
          ? revenueCatCustomerUrl(user.revenueCatId)
          : undefined;
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={() => detailDialog.openWith(user)}
                sx={{ color: "#1976d2" }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {rcUrl && (
              <Tooltip title="Open in RevenueCat">
                <IconButton
                  size="small"
                  href={rcUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  sx={{ color: "#e87722" }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <UserActionsMenu user={user} refreshUsers={refreshList} />
          </Box>
        );
      },
    },
  ];

  const refreshList = () => {
    loadUsers(search, filters);
  };

  const handleLoadNext = () => {
    if (nextToken) {
      loadNextPage();
    }
  };

  const hasSearch = Boolean(search.q);
  const hasFilters = Object.values(filters).some(Boolean);

  const handleClear = () => {
    setSearch(EMPTY_SEARCH);
    setFilters({});
    setResetKey((key) => key + 1);
    loadUsers();
  };

  const suggestedMode = suggestModeFor(search);
  const showEmptyState = hasSearch && !loading && users.length === 0;

  const sortedUsers = [...users].sort((a, b) => {
    if (a.email < b.email) return -1;
    if (a.email > b.email) return 1;
    return 0;
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <DelayedInput
          label="Search users"
          variant="outlined"
          key={`q-${resetKey}`}
          value={search.q ?? ""}
          onDelayedChange={handleQueryChange}
          helperText={activeMode?.hint}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 260 }}
        />
        <TextField
          select
          label="Search by"
          value={search.searchBy}
          onChange={(e) =>
            applySearch({ searchBy: e.target.value as SearchBy })
          }
          sx={{ minWidth: 190 }}
        >
          {SEARCH_MODES.map((mode) => (
            <MenuItem key={mode.value} value={mode.value}>
              {mode.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={refreshList} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary">
          Filters
        </Typography>
        <FilterChip
          label="Provider"
          value={filters.rcStore ?? ""}
          options={RC_STORE_OPTIONS}
          onChange={(value) => applyFilters({ rcStore: value || undefined })}
        />
        <FilterChip
          label="Subscription"
          value={filters.rcStatus ?? ""}
          options={RC_STATUS_OPTIONS}
          onChange={(value) => applyFilters({ rcStatus: value || undefined })}
        />
        {(hasSearch || hasFilters) && (
          <Button
            variant="text"
            size="small"
            onClick={handleClear}
            sx={{ whiteSpace: "nowrap" }}
          >
            Clear filters
          </Button>
        )}
      </Box>

      {truncated && (
        <Alert severity="info">
          Stopped after {users.length} matches. Narrow the term to see the rest.
        </Alert>
      )}

      {showEmptyState ? (
        <Alert
          severity="info"
          action={
            suggestedMode && (
              <Button
                size="small"
                onClick={() => applySearch({ searchBy: suggestedMode })}
              >
                {suggestedMode === "auto" ? "Search Auto" : "Search Email"}
              </Button>
            )
          }
        >
          No users matched “{search.q}” by{" "}
          {activeMode?.label.toLowerCase() ?? "that field"}.
        </Alert>
      ) : (
        <InfinitePaginationTable
          columns={columns}
          items={sortedUsers}
          pageSize={10}
          loading={loading}
          onLoadNext={handleLoadNext}
        />
      )}

      <UserDetailDialog
        open={detailDialog.open}
        user={detailDialog.data}
        onClose={detailDialog.close}
      />
    </Box>
  );
};
